import { useCallback, useMemo, useState } from 'react'

import { AutocompleteInputChangeReason, Divider } from '@mui/material'

import { Body2, Caption, Chip, OverflowInlineList, Stack, Tooltip, useDebouncedValue } from '@collaborate-health/uikit'
import { FormAutocomplete, FormAutocompleteProps } from '@collaborate-health/forms'

import { SearchQualificationVersionsResponse } from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { useQualificationVersionsSearch } from '../../../hooks'

export interface QualificationVersionsAutocompleteProps {
  name?: string
  multiple?: boolean
}

const QualificationVersionsAutocomplete: React.FC<QualificationVersionsAutocompleteProps> = ({
  name = 'QualificationVersionId',
  multiple = false,
}) => {
  const [searchTextQualificationVersions, setSearchTextQualificationVersions] = useState('')
  const debouncedSearchQualifications = useDebouncedValue(searchTextQualificationVersions, 500)

  const handleChangeSearchQualifications = useCallback(
    (event, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'reset') {
        setSearchTextQualificationVersions('')
      } else if (reason === 'input') {
        setSearchTextQualificationVersions(value)
      }
    },
    []
  )

  const { loading, qualifications } = useQualificationVersionsSearch(debouncedSearchQualifications)

  const options = useMemo<FormAutocompleteProps<number, SearchQualificationVersionsResponse>['options']>(() => {
    return qualifications.map((qualification) => ({
      value: qualification.Id,
      name: qualification.Name,
      meta: qualification,
    }))
  }, [qualifications])

  return (
    <FormAutocomplete
      name={name}
      label='Qualification version'
      multiple={multiple}
      options={options}
      loading={loading}
      onInputChange={handleChangeSearchQualifications}
      noOptionsText='No qualification versions found'
      renderOption={(props, { value, meta }) => {
        const qualification = meta!

        return (
          <>
            <li {...props} key={value}>
              <Stack alignItems='stretch' gap={0.5} flexGrow={1} overflow='hidden'>
                <Tooltip text={qualification.Name}>
                  <Stack direction='row' justifyContent='space-between'>
                    <Body2 noWrap>{qualification.Name}</Body2>
                    <Caption noWrap>
                      Valid till:{' '}
                      {qualification.ExpirationDate
                        ? new Date(qualification.ExpirationDate).toLocaleDateString('en-US')
                        : 'Unlimited'}
                    </Caption>
                  </Stack>
                </Tooltip>

                <OverflowInlineList
                  items={qualification.CertificateTypes}
                  direction='row'
                  moreCountPosition='separate'
                  getItemKey={(ct) => ct.Id}
                  renderItem={(ct) => <Chip key={ct.Id} color='default' size='small' label={ct.Name} />}
                  renderHiddenItem={(ct) => (
                    <Body2 key={ct.Id} noWrap>
                      - {ct.Name}
                    </Body2>
                  )}
                />
              </Stack>
            </li>
            <Divider key={`divider-${value}`} component='li' />
          </>
        )
      }}
    />
  )
}

export default QualificationVersionsAutocomplete

